import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;
const EQUIPMENT_TYPES = ['Test Bench','Measurement Instrument','Programming Station','Computer / Workstation',
  'Soldering Station','Label Printer','Barcode Scanner','Camera / Vision System','Power Supply',
  'Climate Chamber','Conveyor / Transport','Hand Tool','Generic'];
const SUPPLIERS = ['gpv','ubiqconn','acmepoint','hk_ringer'];
const SUPPLIER_LABELS = { gpv:'GPV', ubiqconn:'Ubiqconn', acmepoint:'Acmepoint', hk_ringer:'HK Ringer' };

function normalizeRole(r) {
  if (!r) return ''; if (Array.isArray(r)) return (r[0]||'').toLowerCase().trim();
  return String(r).toLowerCase().trim();
}

export default function EquipmentNew() {
  const { getToken } = useAuth(); const { user } = useUser(); const navigate = useNavigate();
  const role = normalizeRole(user?.publicMetadata?.role) || normalizeRole(user?.publicMetadata?.roles);
  const supplierKey = SUPPLIERS.includes(role.replace(/^test-/,'')) ? role.replace(/^test-/,'') : null;
  const canEdit = ['admin','quality-manager','production-engineer','service'].includes(role) || !!supplierKey;

  const [docNum, setDocNum] = useState('');
  const [form, setForm] = useState({
    name:'', type:'Generic', description:'',
    manufacturer:'', model:'', partNumber:'',
    assetId:'', serialNumber:'', location:'',
    calibrationRequired:false, calibrationIntervalDays:'', calibrationStandard:'',
    specifications:[], notes:'',
    visibility:{ suppliers:[], supplierAccess:'None' },
  });
  const [specInput, setSpecInput] = useState({ name:'', value:'', unit:'' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!canEdit) { navigate('/quality/equipment'); return; }
    getToken().then(token =>
      window.fetch(`${API_BASE}/quality/next-doc-number?prefix=EQ-NIN-`, {
        headers:{ Authorization:`Bearer ${token}` },
      }).then(r=>r.json()).then(d=>{ if (d.ok) setDocNum(d.documentNumber); })
    );
  }, []);// eslint-disable-line

  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const toggleSupplier = s => {
    const cur = form.visibility.suppliers;
    const next = cur.includes(s)?cur.filter(x=>x!==s):[...cur,s];
    setForm(f=>({...f,visibility:{...f.visibility,suppliers:next,supplierAccess:next.length?'Specific':'None'}}));
  };
  const addSpec = () => {
    if (!specInput.name.trim()) return;
    setForm(f=>({...f,specifications:[...f.specifications,{...specInput}]}));
    setSpecInput({ name:'',value:'',unit:'' });
  };
  const removeSpec = i => setForm(f=>({...f,specifications:f.specifications.filter((_,j)=>j!==i)}));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError('');
    try {
      const token = await getToken();
      const res = await window.fetch(`${API_BASE}/quality/equipment`, {
        method:'POST', headers:{ Authorization:`Bearer ${token}`,'Content-Type':'application/json' },
        body: JSON.stringify({ ...form, documentNumber: docNum }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error||'Failed');
      navigate(`/quality/equipment/${data.item._id}`);
    } catch(err) { setError(err.message); } finally { setSaving(false); }
  };

  const inp = { background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',
    color:'var(--ni-text)',borderRadius:6,padding:'8px 12px',fontSize:13,width:'100%',boxSizing:'border-box' };
  const labelS = { display:'block',fontSize:11,fontWeight:700,letterSpacing:'0.06em',
    textTransform:'uppercase',color:'var(--ni-text-muted)',marginBottom:5 };

  return (
    <div style={{ maxWidth:700 }}>
      <div style={{ display:'flex',alignItems:'center',gap:12,marginBottom:24 }}>
        <button onClick={()=>navigate('/quality/equipment')}
          style={{ background:'none',border:'none',color:'var(--ni-text-muted)',cursor:'pointer',fontSize:20,padding:0 }}>←</button>
        <h2 style={{ margin:0,fontSize:20,fontWeight:800 }}>Register Equipment</h2>
      </div>

      <form onSubmit={handleSubmit}>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16 }}>
          <div>
            <label style={labelS}>Document Number</label>
            <input value={docNum} readOnly style={{ ...inp,opacity:0.6,cursor:'not-allowed' }} />
          </div>
          <div>
            <label style={labelS}>Type</label>
            <select value={form.type} onChange={e=>set('type',e.target.value)} style={inp}>
              {EQUIPMENT_TYPES.map(t=><option key={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Name *</label>
          <input value={form.name} onChange={e=>set('name',e.target.value)} placeholder="e.g. Fluke 87V Multimeter"
            required style={inp} />
        </div>

        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16,marginBottom:16 }}>
          <div><label style={labelS}>Manufacturer</label>
            <input value={form.manufacturer} onChange={e=>set('manufacturer',e.target.value)} style={inp} /></div>
          <div><label style={labelS}>Model</label>
            <input value={form.model} onChange={e=>set('model',e.target.value)} style={inp} /></div>
          <div><label style={labelS}>Part Number</label>
            <input value={form.partNumber} onChange={e=>set('partNumber',e.target.value)} style={inp} /></div>
        </div>

        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:16,marginBottom:16 }}>
          <div><label style={labelS}>Asset ID / Tag</label>
            <input value={form.assetId} onChange={e=>set('assetId',e.target.value)} placeholder="e.g. NI-0042" style={inp} /></div>
          <div><label style={labelS}>Serial Number</label>
            <input value={form.serialNumber} onChange={e=>set('serialNumber',e.target.value)} style={inp} /></div>
          <div><label style={labelS}>Location</label>
            <input value={form.location} onChange={e=>set('location',e.target.value)} placeholder="e.g. Bench 3" style={inp} /></div>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Description</label>
          <textarea value={form.description} onChange={e=>set('description',e.target.value)}
            rows={3} style={{ ...inp,resize:'vertical' }} />
        </div>

        {/* Calibration */}
        <div style={{ background:'var(--ni-panel)',border:'1px solid var(--ni-border-soft)',borderRadius:10,padding:16,marginBottom:16 }}>
          <div style={{ display:'flex',alignItems:'center',gap:10,marginBottom:12 }}>
            <input type="checkbox" id="calreq" checked={form.calibrationRequired}
              onChange={e=>set('calibrationRequired',e.target.checked)} />
            <label htmlFor="calreq" style={{ fontSize:13,fontWeight:600,cursor:'pointer' }}>Calibration Required</label>
          </div>
          {form.calibrationRequired && (
            <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
              <div><label style={labelS}>Interval (days)</label>
                <input type="number" value={form.calibrationIntervalDays} onChange={e=>set('calibrationIntervalDays',e.target.value)} style={inp} /></div>
              <div><label style={labelS}>Standard / Reference</label>
                <input value={form.calibrationStandard} onChange={e=>set('calibrationStandard',e.target.value)} style={inp} /></div>
            </div>
          )}
        </div>

        {/* Specifications */}
        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Specifications</label>
          {form.specifications.map((s,i)=>(
            <div key={i} style={{ display:'flex',gap:8,alignItems:'center',marginBottom:6,
              background:'rgba(255,255,255,0.03)',padding:'6px 10px',borderRadius:6 }}>
              <span style={{ flex:1,fontSize:13,fontWeight:600 }}>{s.name}</span>
              <span style={{ fontSize:13,color:'var(--ni-text-muted)' }}>{s.value}{s.unit?` ${s.unit}`:''}</span>
              <button type="button" onClick={()=>removeSpec(i)}
                style={{ background:'none',border:'none',color:'#EC2124',cursor:'pointer',fontSize:16,padding:'0 4px' }}>×</button>
            </div>
          ))}
          <div style={{ display:'grid',gridTemplateColumns:'2fr 1fr 80px 36px',gap:8,marginTop:8 }}>
            <input placeholder="Parameter" value={specInput.name} onChange={e=>setSpecInput(s=>({...s,name:e.target.value}))} style={inp} />
            <input placeholder="Value" value={specInput.value} onChange={e=>setSpecInput(s=>({...s,value:e.target.value}))} style={inp} />
            <input placeholder="Unit" value={specInput.unit} onChange={e=>setSpecInput(s=>({...s,unit:e.target.value}))} style={inp} />
            <button type="button" onClick={addSpec}
              style={{ background:'#f59e0b22',color:'#f59e0b',border:'1px solid #f59e0b44',borderRadius:6,
                fontSize:18,cursor:'pointer',fontWeight:700,padding:0 }}>+</button>
          </div>
        </div>

        <div style={{ marginBottom:16 }}>
          <label style={labelS}>Notes</label>
          <textarea value={form.notes} onChange={e=>set('notes',e.target.value)}
            rows={2} style={{ ...inp,resize:'vertical' }} />
        </div>

        <div style={{ marginBottom:24 }}>
          <label style={labelS}>Supplier Visibility</label>
          {supplierKey ? (
            <div style={{ fontSize:13,color:'var(--ni-text-muted)',background:'#f59e0b11',
              border:'1px solid #f59e0b33',borderRadius:8,padding:'10px 12px',lineHeight:1.5 }}>
              🔒 Visible to North Invent and your organisation (<strong style={{ color:'#f59e0b' }}>{SUPPLIER_LABELS[supplierKey]}</strong>).
              North Invent reviews and releases your contribution — visibility is set automatically.
            </div>
          ) : (
            <div style={{ display:'flex',gap:8,flexWrap:'wrap' }}>
              {SUPPLIERS.map(s=>(
                <button type="button" key={s} onClick={()=>toggleSupplier(s)}
                  style={{ padding:'5px 14px',borderRadius:20,fontSize:12,fontWeight:700,cursor:'pointer',border:'1px solid',
                    background:form.visibility.suppliers.includes(s)?'#f59e0b22':'transparent',
                    color:form.visibility.suppliers.includes(s)?'#f59e0b':'var(--ni-text-muted)',
                    borderColor:form.visibility.suppliers.includes(s)?'#f59e0b':'var(--ni-border-soft)' }}>
                  {s.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </div>

        {error && <div style={{ color:'#EC2124',fontSize:13,marginBottom:12 }}>{error}</div>}
        <div style={{ display:'flex',gap:10 }}>
          <button type="submit" disabled={saving}
            style={{ background:'#f59e0b',color:'#000',border:'none',borderRadius:6,
              padding:'10px 24px',fontWeight:700,fontSize:13,cursor:saving?'wait':'pointer' }}>
            {saving?'Registering…':'Register Equipment'}
          </button>
          <button type="button" onClick={()=>navigate('/quality/equipment')}
            style={{ background:'transparent',border:'1px solid var(--ni-border-soft)',color:'var(--ni-text)',
              borderRadius:6,padding:'10px 20px',fontSize:13,cursor:'pointer' }}>Cancel</button>
        </div>
      </form>
    </div>
  );
}
